import { useCallback, useEffect } from "react";
import type { QueueDateRange } from "../shared/helpers";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getAuditQueueUseCases,
  getAuditQueueTasks,
  getAuditQueueResults,
  searchAuditQueueTasks,
  clearQueueSearch,
  setQueueDateRange,
  setCurrentUser,
  AuditQueueUseCase,
  AuditQueueTask,
  AuditQueueResult,
  AuditQueueUser,
} from "src/store/auditQueueSlice";

export interface UseAuditQueueReturn {
  useCases: AuditQueueUseCase[];
  // the date range's tasks (progress counts come from these)
  tasks: AuditQueueTask[];
  // record-ID search results at any date; null while no search result is in
  searchTasks: AuditQueueTask[] | null;
  dateRange: QueueDateRange;
  setDateRange: (range: QueueDateRange) => void;
  // a range change is loading (the old list stays on screen)
  refreshing: boolean;
  // a range change failed; the previous list is still shown
  refreshFailed: boolean;
  // the backend capped the range; only the newest tasks are shown
  truncated: boolean;
  // the record-ID search is waiting on the backend / failed (range tasks are filtered locally)
  searchPending: boolean;
  searchFailed: boolean;
  // run a record-ID search in the database (any date); a blank term clears it
  searchRecords: (term: string) => void;
  results: AuditQueueResult[];
  currentUser: AuditQueueUser;
  isLoading: boolean;
  isError: boolean;
  useCaseById: (id: string) => AuditQueueUseCase | undefined;
  resultsForTask: (taskId: string) => AuditQueueResult[];
  // Compare only: fetch every reviewer's review of one task on demand
  loadResultsForTask: (taskId: string) => void;
  resultsLoading: boolean;
  resultsError: boolean;
}

export function useAuditQueue(search = ""): UseAuditQueueReturn {
  const dispatch = useAppDispatch();

  const {
    useCases,
    tasks,
    results,
    currentUser,
    getUseCasesLoading,
    getTasksLoading,
    getResultsLoading,
    tasksLoaded,
    dateRange,
    truncated,
    searchTasks,
    searchTerm,
    searchLoading,
  } = useAppSelector((state) => state.auditQueueSlice);

  // Read the logged-in user from the existing userSlice and push into auditQueueSlice.
  const { alias, userLoading } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (alias && alias !== currentUser.login) {
      dispatch(
        setCurrentUser({
          login: alias,
          name: alias,
          role: "auditor",
          isElevated: false,
        }),
      );
    }
  }, [alias, currentUser.login, dispatch]);

  // Use cases change rarely: fetch them once.
  useEffect(() => {
    if (alias && getUseCasesLoading === "idle") dispatch(getAuditQueueUseCases());
  }, [dispatch, alias, getUseCasesLoading]);

  // Tasks are reloaded every time the queue opens, so a task submitted or saved
  // in the workspace shows up under the right tab on return. Reviews are not
  // loaded here: the list only needs task.status / review_status, and Compare
  // fetches its one task's reviews on click.
  // A date range change reloads too; counts always cover the selected range.
  useEffect(() => {
    if (!alias) return;
    dispatch(getAuditQueueTasks(dateRange));
  }, [dispatch, alias, dateRange]);

  // Record-ID search runs in the database across every date, when the user submits it.
  // `search` is the submitted term; results for any other term are ignored.
  const term = search.trim();
  const searchRecords = useCallback(
    (value: string) => {
      const t = value.trim();
      dispatch(t ? searchAuditQueueTasks(t) : clearQueueSearch());
    },
    [dispatch],
  );

  const setDateRange = useCallback(
    (range: QueueDateRange) => dispatch(setQueueDateRange(range)),
    [dispatch],
  );

  const loadResultsForTask = useCallback(
    (taskId: string) => {
      dispatch(getAuditQueueResults({ taskIds: [taskId], currentAlias: alias }));
    },
    [dispatch, alias],
  );

  // Treat "idle" as loading too — thunks dispatch in useEffect (after first
  // render), so without this the component briefly flashes the empty state.
  // Once the user slice has resolved and alias is still empty, stop waiting
  // so we do not show a spinner forever.
  const aliasResolved = userLoading === "fulfilled" || userLoading === "rejected";
  // Only the first load shows the spinner; a refresh keeps the current list on
  // screen until the new one arrives.
  const isLoading = !aliasResolved
    ? true
    : getUseCasesLoading === "idle" ||
      getUseCasesLoading === "pending" ||
      (!tasksLoaded && getTasksLoading !== "rejected");

  // A failed refresh keeps the loaded list; only a failed first load is an error page.
  const isError =
    (aliasResolved && !alias) ||
    getUseCasesLoading === "rejected" ||
    (!tasksLoaded && getTasksLoading === "rejected");

  // Selectors (memoised so child components see stable references).
  const useCaseById = useCallback((id: string) => useCases.find((u) => u.id === id), [useCases]);

  const resultsForTask = useCallback(
    (taskId: string) => results.filter((r) => r.task_id === taskId),
    [results],
  );

  return {
    useCases,
    tasks,
    // only results for the submitted term
    searchTasks: term && searchTerm === term ? searchTasks : null,
    dateRange,
    setDateRange,
    refreshing: tasksLoaded && getTasksLoading === "pending",
    refreshFailed: tasksLoaded && getTasksLoading === "rejected",
    truncated,
    results,
    searchPending: !!term && searchLoading !== "rejected" && searchTerm !== term,
    searchFailed: !!term && searchLoading === "rejected",
    searchRecords,
    currentUser,
    isLoading,
    isError,
    useCaseById,
    resultsForTask,
    loadResultsForTask,
    resultsLoading: getResultsLoading === "pending",
    resultsError: getResultsLoading === "rejected",
  };
}
